import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import {
  mdiContentCopy,
  mdiFileDocumentOutline,
  mdiLoading,
} from "@mdi/js";
import { Button, Chip, cn } from "@heroui/react";
import { useState } from "react";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import { COUNTRY_LABELS } from "@/pages/ContractorDetailPage/constants";

interface LegalInfoRow {
  key: string;
  label: string;
  value: string;
  copyable?: boolean;
  missing?: boolean;
}

function r(
  key: string,
  label: string,
  value: string | null | undefined,
  copyable = false,
): LegalInfoRow {
  const v = value?.trim() || null;
  return {
    key,
    label,
    value: v ?? "—",
    copyable: copyable && !!v,
    missing: !v,
  };
}

const LegalInfoTab: React.FC = observer(() => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const { legal } = contractorDetailStore;
  const contractor = contractorDetailStore.contractor;

  if (!contractor) return null;

  const { contract } = legal;
  const { tbank } = legal;
  const uuid = contractor.uuid;

  const handleCopy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      setCopiedKey(null);
    }
  };

  const handleGenerateContract = () => {
    void contract.generateContract(uuid);
  };

  const handleOpenContract = () => {
    void contract.downloadAndOpen(uuid);
  };

  const handleRegisterTBank = () => {
    legal.registerInTBank();
  };

  const buildLegalRows = (): LegalInfoRow[] => {
    const d = legal.status.rawLegalData;
    if (!d) return [];
    return [
      r("legalName", "Сокращённое наименование", d.legal_name),
      r("legalFullName", "Полное наименование", d.legal_full_name),
      r("orgForm", "Форма организации", d.orgForm),
      r("inn", "ИНН", d.inn, true),
      r("ogrn", "ОГРН", d.ogrn, true),
      r("kpp", "КПП", d.kpp, true),
      r("billingDescriptor", "Billing Descriptor", d.billing_descriptor, true),
      r("legalEmail", "E-mail организации", d.legal_email),
      r("siteUrl", "Сайт", d.site_url),
      r(
        "legalCountry",
        "Страна регистрации",
        COUNTRY_LABELS[d.legal_country ?? ""] ?? d.legal_country,
      ),
      r("legalCity", "Город", d.legal_city),
      r("legalStreet", "Улица, дом", d.legal_street),
      r("office", "Офис / помещение", d.office),
      r("legalZip", "Почтовый индекс", d.legal_zip),
      r("directorLastName", "Фамилия руководителя", d.director_last_name),
      r("directorFirstName", "Имя руководителя", d.director_first_name),
      r("directorMiddleName", "Отчество руководителя", d.director_middle_name),
      r("directorPhone", "Телефон руководителя", d.director_phone),
      r(
        "directorCitizenship",
        "Гражданство",
        COUNTRY_LABELS[d.director_citizenship ?? ""] ?? d.director_citizenship,
      ),
      r("bankName", "Банк", d.bank_name),
      r("bik", "БИК", d.bik, true),
      r("corrAccount", "Корр. счёт", d.correspondent_account, true),
      r("settlementAccount", "Расчётный счёт", d.settlement_account, true),
      r("bankPaymentDetails", "Назначение платежа", d.bank_payment_details),
    ];
  };

  const buildSelfEmployedRows = (): LegalInfoRow[] => {
    const d = legal.status.rawContractorData;
    if (!d) return [];
    return [
      r("selfFullName", "ФИО", d.contractor_full_name),
      r("selfInn", "ИНН", d.contractor_inn, true),
      r("selfPhone", "Телефон", d.contractor_phone),
      r(
        "selfPassport",
        "Серия и номер паспорта",
        d.contractor_passport_series && d.contractor_passport_number
          ? `${d.contractor_passport_series} ${d.contractor_passport_number}`
          : null,
        true,
      ),
      r("selfPassportIssuedBy", "Кем выдан", d.contractor_passport_issued_by),
      r("selfPassportDate", "Дата выдачи", d.contractor_passport_issue_date),
    ];
  };

  const rows =
    legal.status.legalType === "selfemployed"
      ? buildSelfEmployedRows()
      : buildLegalRows();

  return (
    <div className="scd-tab-content">
      <div className="scd-legal-tab-header">
        <span className="scd-legal-tab-header__type">
          {legal.status.legalType === "selfemployed"
            ? "Самозанятый"
            : "Юридическое лицо"}
        </span>
        <Button
          variant="tertiary"
          onPress={() => legal.openDrawer()}
          className="scd-legal-tab-header__edit-btn"
        >
          Редактировать
        </Button>
      </div>
      <div className="scd-legal-table">
        <div className="scd-legal-row">
          <span className="scd-legal-row__label">Договор</span>
          <span className="scd-legal-row__value scd-legal-row__value--actions">
            {!contract.contractLink && !contract.isGenerating && (
              <Button
                size="sm"
                variant="primary"
                onPress={handleGenerateContract}
                isDisabled={contract.isLoadingLink}
              >
                Сформировать
              </Button>
            )}
            {contract.isGenerating && (
              <span className="scd-inline-status">
                <Icon
                  path={mdiLoading}
                  size={0.7}
                  className="scd-inline-status__spinner"
                />
                Генерируем...
              </span>
            )}
            {contract.contractLink && !contract.isGenerating && (
              <span className="scd-doc-file">
                <button
                  className="scd-doc-download-btn"
                  title="Открыть договор"
                  onClick={handleOpenContract}
                  disabled={contract.isDownloading}
                >
                  {contract.isDownloading ? (
                    <Icon path={mdiLoading} size={0.8} className="scd-inline-status__spinner" />
                  ) : (
                    <Icon path={mdiFileDocumentOutline} size={0.8} />
                  )}
                </button>
                <button
                  className="scd-doc-file__name"
                  onClick={handleOpenContract}
                  disabled={contract.isDownloading}
                >
                  Договор.pdf
                </button>
              </span>
            )}
          </span>
        </div>
        <div className="scd-legal-row">
          <span className="scd-legal-row__label">Зарегистрирована в Т-Банке</span>
          <span className="scd-legal-row__value scd-legal-row__value--actions">
            {legal.status.isTBankRegistered && (
              <Chip variant="soft" size="sm" color="success">
                Зарегистрирована
              </Chip>
            )}
            {!legal.status.isTBankRegistered && !tbank.isRegistering && (
              <Button
                size="sm"
                variant="primary"
                onPress={handleRegisterTBank}
              >
                Зарегистрировать
              </Button>
            )}
            {tbank.isRegistering && (
              <span className="scd-inline-status">
                <Icon
                  path={mdiLoading}
                  size={0.7}
                  className="scd-inline-status__spinner"
                />
                Регистрируем...
              </span>
            )}
          </span>
        </div>
        {rows.map((row) => (
          <div key={row.key} className="scd-legal-row">
            <span className="scd-legal-row__label">{row.label}</span>
            <span
              className={cn(
                "scd-legal-row__value",
                row.missing && "scd-legal-row__value--missing",
              )}
            >
              {row.value}
              {row.copyable && (
                <button
                  className={cn(
                    "scd-copy-btn",
                    copiedKey === row.key && "scd-copy-btn--copied",
                  )}
                  onClick={() => handleCopy(row.key, row.value)}
                  title="Скопировать"
                >
                  <Icon path={mdiContentCopy} size={0.55} />
                  {copiedKey === row.key && (
                    <span className="scd-copy-btn__tooltip">Скопировано</span>
                  )}
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});

LegalInfoTab.displayName = "LegalInfoTab";
export default LegalInfoTab;
